import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE,
  SR_API_ACTION_UTILITY_PARTNER_CREATE_BCC_REMINDER,
} from 'api/utilityActionTypes';

import { UTILITY_ACTIVATION_SOURCE_BCC_REMINDER } from 'common/utility/constants';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';

const getFormFields = ({
  formValues = {},
  utilityPartnerList,
  addressMatches,
  onAutocompleteAddress,
}) => {
  return [
    {
      title: 'Utility Partner',
      id: 'utilityPartnerId',
      type: 'select',
      properties: {
        placeholder: '--',
        enableResetOption: true,
        transformToNumber: true,
        choices: (utilityPartnerList || []).map(({ name, id }) => ({
          label: name,
          value: id,
        })),
      },
      validations: ['required'],
    },
    {
      id: 'email',
      title: 'Renter Email',
      type: 'email',
      properties: {
        placeholder: 'type email here...',
      },
      validations: ['required', 'email'],
    },
    {
      id: 'phone',
      title: 'Renter Phone',
      type: 'phone',
      properties: {
        placeholder: 'type phone here...',
      },
    },
    {
      id: 'firstName',
      title: 'Renter First Name',
      type: 'short_text',
      properties: {
        placeholder: 'type first name here...',
      },
    },
    {
      id: 'lastName',
      title: 'Renter Last Name',
      type: 'short_text',
      properties: {
        placeholder: 'type last name here...',
      },
    },
    {
      id: 'addressInput',
      title: 'Address',
      type: 'autocomplete',
      properties: {
        placeholder: 'type address here...',
        choices: (addressMatches || []).map(({ display }) => display),
        onSuggestionsFetchRequested: onAutocompleteAddress,
        onChange: ({ newValue, formikBag }) => {
          const addressMatch = (addressMatches || []).find(({ display }) => newValue === display);
          formikBag.setFieldValue('addressId', addressMatch ? addressMatch.id : null);
        },
        filterFunc: () => {
          return true;
        },
        disabled: !formValues.utilityPartnerId,
      },
    },
    {
      id: 'addressId',
      type: 'hidden',
    },
    {
      id: 'leaseStartDate',
      title: 'Renter Lease Start Date',
      type: 'date_picker',
      properties: {
        placeholder: 'Select lease start date...',
      },
    },
  ];
};

const SrUtilityProfitAdminBccReminderForm = ({ utilityPartnerList, dispatch }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressMatches, setAddressMatches] = useState([]);
  const lastAutocompleteSearchRef = useRef(null);

  const onSubmit = (formValues, formikBag) => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_CREATE_BCC_REMINDER,
      payload: {
        utilityPartnerId: formValues.utilityPartnerId,
        addressId: formValues.addressId,
        leaseStartDate: formValues.leaseStartDate || undefined,
        user: {
          id: formValues.email,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          email: formValues.email,
          phone: formValues.phone,
        },
        source: UTILITY_ACTIVATION_SOURCE_BCC_REMINDER,
      },
      onSuccess() {
        formikBag.resetForm();
        setIsSubmitting(false);
        global.alert('Event created successfully.');
      },
      onError(error) {
        global.alert(error.message);
      },
    });
  };

  return (
    <Formik
      onSubmit={(formValues, formikBag) => {
        setIsSubmitting(true);
        onSubmit(formValues, formikBag);
      }}
      initialValues={createInitialValues({
        formFields: getFormFields({
          utilityPartnerList,
        }),
        defaultValues: {},
      })}
      validate={(formValues) => {
        const formFields = getFormFields({
          utilityPartnerList,
        });
        return createFormValidationFn(formFields)(formValues);
      }}
      render={(formikBag) => {
        const formFields = getFormFields({
          formValues: formikBag.values,
          utilityPartnerList,
          addressMatches,
          onAutocompleteAddress: ({ value }) => {
            lastAutocompleteSearchRef.current = value;
            dispatch(requestUtilityApi, {
              action: SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE,
              queryId: value,
              payload: {
                input: value,
                utilityPartnerId: formikBag.values.utilityPartnerId,
                withRequiredTypes: true,
              },
            }).then((addresses) => {
              if (lastAutocompleteSearchRef.current === value) {
                setAddressMatches(addresses);
              }
            });
          },
        });

        return (
          <Form>
            <SrBox marginTop="20px">
              <div>
                {formFields.map((field) => (
                  <SrFormikField
                    key={field.id}
                    formField={field}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
              </div>
              <SrBox margin="20px 0">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  onClick={formikBag.submitForm}
                  disabled={isSubmitting}
                >
                  Submit
                </SrButton>
              </SrBox>
            </SrBox>
          </Form>
        );
      }}
    />
  );
};

SrUtilityProfitAdminBccReminderForm.propTypes = {
  utilityPartnerList: PropTypes.array,
  dispatch: PropTypes.func,
};

SrUtilityProfitAdminBccReminderForm.displayName = 'SrUtilityProfitAdminBccReminderForm';

export default SrUtilityProfitAdminBccReminderForm;
